// Storage Detection Service - Storage Availability Testing & Environment Detection
// Extracted from userDataPersistence.js for better separation of concerns

import { debugLog } from '../debugUtils';

// Storage mechanism availability cache
let storageAvailability = null;

/**
 * Test actual storage mechanism availability
 */
const testStorageAvailability = () => {
  const results = {
    localStorage: false,
    sessionStorage: false,
    cookies: false,
  };

  // Test localStorage
  try {
    const testKey = '__swts_test_ls__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    results.localStorage = true;
  } catch (e) {
    // localStorage not available or failed
  }

  // Test sessionStorage
  try {
    const testKey = '__swts_test_ss__';
    sessionStorage.setItem(testKey, 'test');
    sessionStorage.removeItem(testKey);
    results.sessionStorage = true;
  } catch (e) {
    // sessionStorage not available or failed
  }

  // Test cookies
  try {
    const testKey = '__swts_test_cookie__';
    document.cookie = `${testKey}=test; path=/; max-age=1`;
    const cookieSet = document.cookie.includes(testKey);
    if (cookieSet) {
      document.cookie = `${testKey}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
      results.cookies = true;
    }
  } catch (e) {
    // Cookies not available
  }

  return results;
};

/**
 * Emergency rollback: Check if encryption should be disabled
 * Can be controlled via environment variable or localStorage flag
 */
export const shouldDisableEncryption = () => {
  // Environment variable override (for emergency rollback)
  if (process.env.GATSBY_DISABLE_ENCRYPTION === 'true') {
    debugLog(
      '🚨 Encryption disabled via environment variable (emergency rollback)'
    );
    return true;
  }

  // localStorage flag for emergency rollback
  try {
    if (
      typeof window !== 'undefined' &&
      localStorage.getItem('swts_disable_encryption') === 'true'
    ) {
      debugLog(
        '🚨 Encryption disabled via localStorage flag (emergency rollback)'
      );
      return true;
    }
  } catch (e) {
    // localStorage not available
  }

  return false;
};

/**
 * Detect the storage environment and determine encryption strategy
 * STRATEGY: Prioritize OTO functionality, add security where environment allows
 * EMERGENCY: Can be disabled via environment variable or localStorage flag
 */
export const detectStorageEnvironment = () => {
  if (typeof window === 'undefined') {
    return { strategy: 'PLAIN_TEXT', reason: 'server_side' };
  }

  // Emergency rollback check
  if (shouldDisableEncryption()) {
    return { strategy: 'PLAIN_TEXT', reason: 'emergency_rollback_disabled' };
  }

  // Check user agent for known mobile app webviews
  const userAgent = navigator.userAgent;
  const isFacebookApp = /fbav|fban/i.test(userAgent);
  const isInstagramApp = /instagram/i.test(userAgent);
  const isWebView = /wv|WebView/i.test(userAgent);
  const isMobileApp = isFacebookApp || isInstagramApp || isWebView;

  // Test actual storage availability
  const storageTest = testStorageAvailability();

  // Determine strategy based on environment
  if (!isMobileApp && storageTest.sessionStorage && storageTest.localStorage) {
    // Desktop/mobile browsers with full storage
    return {
      strategy: 'ENCRYPTED',
      reason: 'full_storage_available',
      details: { isMobileApp, storageTest },
    };
  } else if (isMobileApp && !storageTest.sessionStorage) {
    // Facebook/Instagram apps with restricted storage
    return {
      strategy: 'PLAIN_TEXT',
      reason: 'mobile_app_restricted_storage',
      details: { isFacebookApp, isInstagramApp, isWebView, storageTest },
    };
  } else if (storageTest.sessionStorage || storageTest.localStorage) {
    // Partial storage available - try minimal encryption
    return {
      strategy: 'MINIMAL_ENCRYPTION',
      reason: 'partial_storage_available',
      details: { isMobileApp, storageTest },
    };
  } else {
    // No storage available - fallback to plain text for functionality
    return {
      strategy: 'PLAIN_TEXT',
      reason: 'no_storage_available',
      details: { isMobileApp, storageTest },
    };
  }
};

/**
 * Detect which storage mechanisms are available in the current browser
 */
const detectStorageAvailability = () => {
  if (typeof window === 'undefined') {
    return {
      localStorage: false,
      sessionStorage: false,
      cookies: false,
      urlParams: false,
      inMemory: true,
    };
  }

  const availability = {
    localStorage: false,
    sessionStorage: false,
    cookies: false,
    urlParams: true, // URL params are always available in browser
    inMemory: true, // In-memory is always available
  };

  // Test localStorage
  try {
    const testKey = '__swts_test__';
    window.localStorage.setItem(testKey, 'test');
    window.localStorage.removeItem(testKey);
    availability.localStorage = true;
  } catch (e) {
    // localStorage not available (private browsing, disabled, etc.)
  }

  // Test sessionStorage
  try {
    const testKey = '__swts_test__';
    window.sessionStorage.setItem(testKey, 'test');
    window.sessionStorage.removeItem(testKey);
    availability.sessionStorage = true;
  } catch (e) {
    // sessionStorage not available
  }

  // Test cookies
  try {
    const testKey = '__swts_test__';
    document.cookie = `${testKey}=test; path=/; max-age=1`;
    const cookieSet = document.cookie.includes(testKey);
    if (cookieSet) {
      // Clean up test cookie
      document.cookie = `${testKey}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
      availability.cookies = true;
    }
  } catch (e) {
    // Cookies not available
  }

  return availability;
};

/**
 * Get storage availability (cached)
 */
export const getStorageAvailability = () => {
  if (!storageAvailability) {
    storageAvailability = detectStorageAvailability();
    debugLog('📊 Storage availability detected:', storageAvailability);
  }
  return storageAvailability;
};

/**
 * Clear storage availability cache (for test isolation)
 */
export const clearStorageAvailabilityCache = () => {
  storageAvailability = null;
  debugLog('🗑️ Storage availability cache cleared');
};
